import { useState } from 'react';
import type { RecurrenceRule } from '@tradeoutlet/shared';
import {
  REPEAT_CREATE_OPTIONS, REPEAT_DAYS, REPEAT_ENDS_OPTIONS, REPEAT_FREQ_OPTIONS, REPEAT_NOTE, recurrencePattern, recurrenceText,
  repeatForm, repeatMonthlyOptions, repeatPreview, repeatProblem, repeatRule, repeatUnit, type RepeatForm,
} from '@tradeoutlet/shared';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Button, Field, Input, Modal, Select } from '@/components/ui';

/** Activity feed wording ("set it to repeat every week on Mon"). */
export function describeRecurrence(r: RecurrenceRule | null) {
  if (!r) return 'Does not repeat';
  return `${recurrencePattern(r)}${r.until ? ` until ${r.until}` : ''}`;
}

const today = () => DateTime.now().setZone('Europe/London').toISODate()!;
const fmt = (d: string) => DateTime.fromISO(d).setLocale('en-GB').toFormat('ccc d LLL yyyy');

/** The repeat form (t335u): how often, which days, monthly mode, when it ends and when the next one appears. */
export function RepeatFields({ form, onChange, anchor }: { form: RepeatForm; onChange: (f: RepeatForm) => void; anchor: string }) {
  const set = (p: Partial<RepeatForm>) => onChange({ ...form, ...p });
  const n = Math.max(1, Number(form.interval) || 1);
  const rule = repeatRule(form, anchor);
  const preview = repeatPreview(rule, anchor);
  return (
    <div className="space-y-3">
      <Field label="Repeats">
        <Select data-testid="repeat-freq" value={form.freq} onChange={e => set({ freq: e.target.value as RepeatForm['freq'] })}>
          {REPEAT_FREQ_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
      </Field>
      {form.freq && <>
        <Field label="Every">
          <span className="flex items-center gap-2">
            <Input data-testid="repeat-interval" type="number" min={1} max={365} className="w-20" value={form.interval} onChange={e => set({ interval: e.target.value })} />
            <span className="text-sm text-slate-500">{repeatUnit(form.freq, n)}</span>
          </span>
        </Field>
        {form.freq === 'weekly' && <Field label="On">
          <div className="flex flex-wrap gap-1">
            {REPEAT_DAYS.map((d, i) => {
              const on = form.days.includes(i + 1);
              return <button type="button" key={d} data-testid={`repeat-day-${i + 1}`} aria-pressed={on}
                onClick={() => set({ days: on ? form.days.filter(x => x !== i + 1) : [...form.days, i + 1].sort() })}
                className={clsx('h-8 w-11 rounded-md border text-xs font-medium', on ? 'border-black bg-accent text-on-accent-strong' : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300')}>{d}</button>;
            })}
          </div>
        </Field>}
        {form.freq === 'monthly' && <Field label="On">
          <Select data-testid="repeat-monthly" value={form.monthly} onChange={e => set({ monthly: e.target.value as RepeatForm['monthly'] })}>
            {repeatMonthlyOptions(anchor).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        </Field>}
        <Field label="Ends">
          <span className="flex flex-wrap items-center gap-2">
            <Select data-testid="repeat-ends" className="w-56" value={form.ends} onChange={e => set({ ends: e.target.value as RepeatForm['ends'] })}>
              {REPEAT_ENDS_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            {form.ends === 'on' && <Input data-testid="repeat-until" type="date" className="w-44" value={form.until} onChange={e => set({ until: e.target.value })} />}
            {form.ends === 'after' && <><Input data-testid="repeat-count" type="number" min={2} max={999} className="w-20" value={form.count} onChange={e => set({ count: e.target.value })} /><span className="text-sm text-slate-500">times in all</span></>}
          </span>
        </Field>
        <Field label="Next one appears">
          <Select data-testid="repeat-create" value={form.create} onChange={e => set({ create: e.target.value as RepeatForm['create'] })}>
            {REPEAT_CREATE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        </Field>
        <p className="text-xs text-slate-500">{REPEAT_NOTE}</p>
        {preview.length > 0 && <p data-testid="repeat-preview" className="text-sm text-slate-600 dark:text-slate-300">After {fmt(anchor)}: {preview.map(fmt).join(', ')}{preview.length === 3 ? '…' : ''}</p>}
      </>}
    </div>
  );
}

/**
 * Recurring tasks: a quiet summary line plus "Change", which opens the repeat form. By default the next instance is
 * created when this one is completed; "On its date" series are created by the worker on the due date.
 */
export function RecurrenceEditor({ value, onChange, disabled, due }: { value: RecurrenceRule | null; onChange: (r: RecurrenceRule | null) => void; disabled?: boolean; due?: string | null }) {
  const anchor = due || today();
  const [form, setForm] = useState<RepeatForm | null>(null);
  const problem = form ? repeatProblem(form, !!due) : null;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span data-testid="repeat-summary" className={clsx('text-sm', value ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500')}>{value ? recurrenceText(value, due) : 'Does not repeat'}</span>
      {!disabled && <button type="button" data-testid="repeat-change" className="text-sm underline decoration-slate-400 underline-offset-2 hover:decoration-black" onClick={() => setForm(repeatForm(value, anchor))}>{value ? 'Change' : 'Set up'}</button>}
      <Modal open={!!form} onClose={() => setForm(null)} title="Repeat"
        footer={<>
          {problem && form?.freq && <span className="mr-auto text-sm text-slate-600">{problem}</span>}
          <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
          <Button data-testid="repeat-save" disabled={!!(form?.freq && problem)} onClick={() => { if (form) onChange(repeatRule(form, anchor, value)); setForm(null); }}>Save</Button>
        </>}>
        {form && <RepeatFields form={form} onChange={setForm} anchor={anchor} />}
      </Modal>
    </span>
  );
}
